import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Spinner,
  Flex,
  Skeleton,
  Icon,
} from "@chakra-ui/react";

import { useNotificationContext } from "../../../../Contexts/NotificationContext";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import { useUserContext } from "../../../../Contexts/userContext";
import { SendNotificationRequest, User } from "../../../../services/Dtos";
import useThemeColors from "../../../../hooks/useThemeColors";
import useToaster from "../../../../hooks/useToaster";
import CustomAlert from "../../../CustomUi/CustomAlert";
import SectionHeader from "../../../CustomUi/SectionHeader";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import CustomModal from "../../../CustomUi/CustomModal";
import CustomButton from "../../../CustomUi/CustomButton";
import SelectInput from "../../../CustomUi/SelectInput";
import TextAreaInput from "../../../CustomUi/TextAreaInput";
import { notificationTypeOptions } from "../../../../services/constants";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useFormValidation } from "../../../../hooks/useFormValidation";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { NotificationType } from "../../../../services/Enums";
import { formatTimeAgo } from "../../../../services/Utils";

const NotificationSettings: React.FC = () => {
  const { user } = useUserContext();
  const { users } = useSchoolsContext();
  const {
    sendNotification,
    notifications,
    fetchNotifications,
    isLoading,
    markAsRead,
    deleteNotification,
    markAllAsRead,
    clearAllNotifications,
    getNotificationTypeLabel,
  } = useNotificationContext();
  const colors = useThemeColors();
  const { showSuccessToast, showErrorToast } = useToaster();
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const icons = useIcons();

  // Form state
  const [formData, setFormData] = useState<SendNotificationRequest>({
    title: "",
    message: "",
    recipientId: "",
    type: NotificationType.DirectMessage,
  });

  const [, setTouchedFields] = useState<Set<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const [selectedType, setSelectedType] = useState<NotificationType | "all">(
    "all"
  );

  // Sender cache for performance
  const [senderCache, setSenderCache] = useState<Map<string, User>>(new Map());
  const [senderLoading] = useState<Set<string>>(new Set());
  const processedNotifications = useRef<Set<string>>(new Set());

  const isAdmin = user?.role === 3;

  // Form field configuration for validation
  const formFields = {
    title: { type: "text" as const, required: true, maxLength: 100 },
    message: { type: "text" as const, required: true, maxLength: 500 },
    type: { type: "select" as const, required: true },
    recipientId: {
      type: "select" as const,
      required: false,
      customValidation: (value: string) => {
        if (formData.type === NotificationType.DirectMessage && !value) {
          return "Please select a recipient for direct messages";
        }
        return "";
      },
    },
  };

  // Custom hooks
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setFormData,
    setTouchedFields,
    integerFields: ["type"],
  });

  const { errors, clearErrors, setFieldError } = useFormValidation();

  const { hasChanges, validateAllFields, hasValidationErrors } = useFormChanges(
    {
      fields: formFields,
      currentData: formData,
      isEditMode: false,
    }
  );

  // Clear errors when modal opens
  React.useEffect(() => {
    if (isOpen) {
      clearErrors();
    }
  }, [isOpen]);

  const handleModalClose = useCallback(() => {
    setFormData({
      title: "",
      message: "",
      recipientId: "",
      type: NotificationType.DirectMessage,
    });
    setTouchedFields(new Set());
    clearErrors();
    onClose();
  }, [clearErrors, onClose]);

  // Get sender information from already-loaded users
  const getSenderInfo = useCallback(
    (notification: any) => {
      if (notification.type === NotificationType.SystemMessage) {
        return;
      }

      if (notification.senderId && !senderCache.has(notification.senderId)) {
        const senderUser = users.find(
          (user) => user.id === notification.senderId
        );
        if (senderUser) {
          setSenderCache((prev) =>
            new Map(prev).set(notification.senderId, senderUser)
          );
        }
      }
    },
    [users, senderCache]
  );

  // Get sender display for a notification
  const getSenderDisplay = useCallback(
    (notification: any) => {
      if (notification.type === NotificationType.SystemMessage) {
        return "Result.ng";
      }

      if (senderLoading.has(notification.senderId)) {
        return <Skeleton height="14px" width="80px" />;
      }

      const sender = senderCache.get(notification.senderId);
      if (sender) {
        return `${sender.firstName} ${sender.lastName} (${sender.email})`;
      }

      return "Unknown User";
    },
    [senderCache, senderLoading]
  );

  // Process sender info for all notifications when they change
  React.useEffect(() => {
    notifications.forEach((notification) => {
      if (!processedNotifications.current.has(notification.id)) {
        processedNotifications.current.add(notification.id);
        getSenderInfo(notification);
      }
    });
  }, [notifications, getSenderInfo]);

  // Enhanced field change handler
  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      handleFieldChange(e);

      // Clear field error when user starts typing
      const { name } = e.target;
      if (errors[name]) {
        setFieldError(name, "");
      }
    },
    [handleFieldChange, errors, setFieldError]
  );

  const handleSendNotification = useCallback(async () => {
    clearErrors();

    // Validate all fields
    const validationErrors = validateAllFields();

    if (hasValidationErrors(validationErrors)) {
      // Set errors for display
      Object.entries(validationErrors).forEach(([field, error]) => {
        if (error) setFieldError(field, error);
      });

      showErrorToast("Please fix the validation errors");
      return;
    }

    setIsSending(true);
    try {
      const result = await sendNotification(formData);
      if (result?.succeeded) {
        showSuccessToast("Notification sent successfully");
        handleModalClose();
        fetchNotifications();
      } else {
        showErrorToast("Failed to send notification");
      }
    } catch (error) {
      showErrorToast("Error sending notification");
    } finally {
      setIsSending(false);
    }
  }, [
    formData,
    clearErrors,
    validateAllFields,
    hasValidationErrors,
    setFieldError,
    showErrorToast,
    showSuccessToast,
    sendNotification,
    handleModalClose,
    fetchNotifications,
  ]);

  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markAsRead(notificationId);
        showSuccessToast("Notification marked as read");
      } catch (error) {
        showErrorToast("Failed to mark notification as read");
      }
    },
    [markAsRead, showSuccessToast, showErrorToast]
  );

  const handleDeleteNotification = useCallback(
    async (notificationId: string) => {
      try {
        const success = await deleteNotification(notificationId);
        if (success) {
          showSuccessToast("Notification deleted successfully");
        } else {
          showErrorToast("Failed to delete notification");
        }
      } catch (error) {
        showErrorToast("Error deleting notification");
      }
    },
    [deleteNotification, showSuccessToast, showErrorToast]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      showSuccessToast("All notifications marked as read");
    } catch (error) {
      showErrorToast("Failed to mark all notifications as read");
    }
  }, [markAllAsRead, showSuccessToast, showErrorToast]);

  const handleClearAll = useCallback(async () => {
    try {
      await clearAllNotifications();
      showSuccessToast("All notifications cleared");
    } catch (error) {
      showErrorToast("Failed to clear all notifications");
    }
  }, [clearAllNotifications, showSuccessToast, showErrorToast]);

  const filteredNotifications =
    selectedType === "all"
      ? notifications
      : notifications.filter((n) => n.type === Number(selectedType));

  const unreadCount = filteredNotifications.filter((n) => !n.isRead).length;

  const userOptions = users.map((user) => ({
    label: `${user.firstName} ${user.lastName} (${user.email})`,
    value: user.id,
  }));

  const notificationTypeFormOptions = [
    {
      label: "Direct Message",
      value: NotificationType.DirectMessage.toString(),
    },
    {
      label: "Broadcast to All",
      value: NotificationType.BroadcastMessage.toString(),
    },
  ];

  if (!isAdmin) {
    return (
      <Box p={4}>
        <CustomAlert
          status="warning"
          title="Access Denied"
          message="Only administrators can access notification settings."
        />
      </Box>
    );
  }

  return (
    <VStack gap={6}>
      {/* Compose New Notification */}
      <Cad>
        <SectionHeader
          title="Compose New Notification"
          subtitle="Send personal direct messages to users or broadcast announcements to everyone"
          icon={icons.utility.chat}
          buttonText="New Message"
          onButtonClick={onOpen}
          buttonIcon={icons.utility.send}
          buttonColorScheme={colors.schoolAccent.primary}
        />
      </Cad>

      {/* Notification History */}
      <Cad>
        <SectionHeader
          title="Notification History"
          subtitle="View and manage your notifications"
          icon={icons.utility.notification}
        />
        <HStack
          mb={4}
          p={3}
          border="1px"
          borderColor={colors.border.primary}
          borderRadius="md"
          justify={"space-between"}
          gap={8}
          flexDir={["column", "row"]}
          align={["stretch", "center"]}
        >
          <Flex
            direction={["column", "row"]}
            justify="space-between"
            align={["start", "center"]}
            gap={3}
          >
            <HStack gap={2} w={["full", "auto"]}>
              <SelectInput
                collection={notificationTypeOptions}
                value={selectedType.toString()}
                onChange={(e) =>
                  setSelectedType(e.target.value as NotificationType | "all")
                }
                label=""
                name="type"
                placeholder="All Types"
                size="sm"
                minW="200px"
              />
              <IconButton
                aria-label="Refresh notifications"
                size="sm"
                variant="outline"
                onClick={fetchNotifications}
                loading={isLoading}
              >
                <Icon as={icons.utility.refresh} />
              </IconButton>
            </HStack>
          </Flex>

          {/* Bulk Actions */}
          {(unreadCount > 0 || filteredNotifications.length > 0) && (
            <Flex
              direction={["column", "row"]}
              justify="space-between"
              align={["start", "center"]}
              gap={3}
            >
              <Text fontSize="sm" fontWeight="medium">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${
                      unreadCount > 1 ? "s" : ""
                    }`
                  : "All notifications read"}
              </Text>
              <HStack
                gap={2}
                w={["full", "auto"]}
                justify={["space-between", "flex-end"]}
              >
                {unreadCount > 0 && (
                  <CustomButton
                    size="xs"
                    variant="outline"
                    label="Mark all read"
                    icon={icons.utility.check}
                    onClick={handleMarkAllAsRead}
                  />
                )}
                {filteredNotifications.length > 0 && (
                  <CustomButton
                    size="xs"
                    variant="outline"
                    colorPalette="red"
                    label="Clear all"
                    icon={icons.utility.delete}
                    onClick={handleClearAll}
                  />
                )}
              </HStack>
            </Flex>
          )}
        </HStack>

        <VStack>
          {isLoading ? (
            <Flex justify="center" p={6}>
              <Spinner size="md" />
            </Flex>
          ) : filteredNotifications.length === 0 ? (
            <Text color={colors.text.secondary} textAlign="center" py={6}>
              No notifications found
            </Text>
          ) : (
            <VStack gap={3} align="stretch" w="full">
              {filteredNotifications.map((notification) => {
                const IconComponent = getNotificationTypeLabel(
                  notification.type
                ).icon;
                const colorPalette = getNotificationTypeLabel(
                  notification.type
                ).color;

                return (
                  <Box
                    key={notification.id}
                    p={4}
                    border="1px solid"
                    borderColor={colors.border.primary}
                    borderRadius="md"
                    bg={
                      notification.isRead
                        ? colors.background.primary
                        : colors.background.secondary
                    }
                    position="relative"
                    borderLeft={
                      !notification.isRead ? "4px solid" : "1px solid"
                    }
                    borderLeftColor={
                      !notification.isRead
                        ? `${colorPalette}.500`
                        : colors.border.primary
                    }
                  >
                    <HStack gap={3} align="flex-start">
                      {/* Icon */}
                      <Box
                        p={2}
                        borderRadius="lg"
                        bg={`${colorPalette}.100`}
                        color={`${colorPalette}.600`}
                        flexShrink={0}
                      >
                        <IconComponent size={16} />
                      </Box>

                      {/* Content */}
                      <VStack gap={2} align="flex-start" flex={1} minW={0}>
                        <Flex
                          direction={["column", "row"]}
                          justify="space-between"
                          align={["start", "center"]}
                          gap={2}
                          w="full"
                        >
                          <VStack align="start" gap={2}>
                            <HStack gap={2} wrap="wrap">
                              <Text
                                fontWeight={
                                  notification.isRead ? "normal" : "semibold"
                                }
                                fontSize="sm"
                                color={colors.text.primary}
                              >
                                {notification.title}
                              </Text>
                              <Badge
                                colorPalette={colorPalette}
                                size="sm"
                                variant="subtle"
                              >
                                {
                                  getNotificationTypeLabel(notification.type)
                                    .label
                                }
                              </Badge>
                              {!notification.isRead && (
                                <Badge colorPalette="red" size="sm">
                                  Unread
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                          <Text
                            fontSize="xs"
                            color={colors.text.tertiary}
                            flexShrink={0}
                          >
                            {formatTimeAgo(notification.createdAt)}
                          </Text>
                        </Flex>

                        {/* Sender information */}
                        <Text
                          fontSize="xs"
                          color={colors.text.tertiary}
                          fontWeight="medium"
                        >
                          From: {getSenderDisplay(notification)}
                        </Text>

                        {/* Message */}
                        <Box w="full">
                          <Text fontSize="sm" color={colors.text.secondary}>
                            {notification.message}
                          </Text>
                        </Box>

                        {/* Actions */}
                        <HStack gap={2} w="full" justify={"flex-end"}>
                          {!notification.isRead && (
                            <IconButton
                              aria-label="Mark as read"
                              size="xs"
                              variant="outline"
                              onClick={() => handleMarkAsRead(notification.id)}
                              colorPalette="green"
                            >
                              <Icon as={icons.utility.check} />
                            </IconButton>
                          )}
                          <IconButton
                            aria-label="Delete notification"
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              handleDeleteNotification(notification.id)
                            }
                            colorPalette="red"
                          >
                            <Icon as={icons.utility.delete} />
                          </IconButton>
                        </HStack>
                      </VStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </VStack>
      </Cad>

      {/* Compose Modal */}
      <CustomModal
        isOpen={isOpen}
        onClose={handleModalClose}
        handleClose={handleModalClose}
        handleAction={handleSendNotification}
        isLoading={isSending}
        hasChanges={hasChanges}
        hasValidationErrors={Object.keys(errors).length > 0}
        isDisabled={isSending || Object.keys(errors).length > 0}
        title="Compose New Notification"
        icon={icons.utility.send}
        mode="add"
        actionText="Send Notification"
      >
        {Object.values(errors)
          .slice(0, 3)
          .map((error, index) => (
            <CustomAlert key={index} message={error} />
          ))}
        <VStack gap={6} align="stretch">
          <SelectInput
            label="Notification Type"
            name="type"
            value={formData.type.toString()}
            onChange={handleInputChange}
            collection={notificationTypeFormOptions}
            placeholder="Choose notification type"
            required
            error={errors.type}
            invalid={!!errors.type}
          />

          {formData.type === NotificationType.DirectMessage && (
            <SelectInput
              label="Recipient"
              name="recipientId"
              value={formData.recipientId}
              onChange={handleInputChange}
              collection={userOptions}
              placeholder="Select recipient"
              required
              error={errors.recipientId}
              invalid={!!errors.recipientId}
            />
          )}

          <TextAreaInput
            label="Notification Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            helperText={`${100 - formData.title.length} characters remaining`}
            maxLength={100}
            rows={2}
            required
            error={errors.title}
            invalid={!!errors.title}
          />

          <TextAreaInput
            label="Notification Message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            helperText={`${500 - formData.message.length} characters remaining`}
            rows={5}
            maxLength={500}
            required
            error={errors.message}
            invalid={!!errors.message}
          />
        </VStack>
      </CustomModal>
    </VStack>
  );
};

export default NotificationSettings;
